import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  PlusIcon,
  TrophyIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ColorInput, BackgroundInput } from 'components/ColorPickers'
import { ImageUpload } from 'components/ImageUpload'
import { RollingNumber } from 'components/RollingNumber'
import { ScoreCelebration, SCOREBUG_COVER_MS, SCOREBUG_HOLD_MS } from 'components/ScoreCelebration'
import { BackgroundImageInput, fitToObjectFit } from 'components/BackgroundImageInput'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ScaledStage, CenteredOnCanvas } from 'components/ScaledStage'
import { SCOREBOARD, elementFontStack } from 'lib/elementCanvas'
import {
  ChatFontKey,
  normalizeChatFontKey
} from 'lib/chatFonts'
import { FontSelect } from 'components/FontSelect'
import { useFontGate } from 'lib/fontLoader'
import { sortByName } from 'lib/elementOrder'
import { useAuth } from 'context/user'
import { useEventDetail } from 'hooks/useEventDetail'
import { useYouTubeConnection } from 'hooks/useYouTubeConnection'
import { graphqlRequest } from 'lib/helpers'
import {
  ScoreboardSummary,
  Competitor,
  CompetitorInput,
  DeletionResult,
  ImageFit
} from 'types/api'
import createScoreboardMutation from 'graphql/createScoreboard.graphql'
import updateScoreboardMutation from 'graphql/updateScoreboard.graphql'
import deleteScoreboardMutation from 'graphql/deleteScoreboard.graphql'
import duplicateScoreboardMutation from 'graphql/duplicateScoreboard.graphql'
import addCompetitorMutation from 'graphql/addCompetitor.graphql'
import updateCompetitorMutation from 'graphql/updateCompetitor.graphql'
import removeCompetitorMutation from 'graphql/removeCompetitor.graphql'

const MAX_COMPETITORS = 8

const DEFAULT_BG = 'linear-gradient(135deg, #1E3A8A 0%, #4338CA 100%)'
const DEFAULT_TITLE_TEXT_COLOR = '#FFFFFF'
const DEFAULT_DIVIDER_COLOR = '#FFFFFF'
const DEFAULT_NAME_TEXT_COLOR = '#FFFFFF'
const DEFAULT_SCORE_TEXT_COLOR = '#67E8F9'

const DEFAULT_COMPETITORS: Array<{ name: string }> = [
  { name: 'Team A' },
  { name: 'Team B' }
]

const DEFAULT_CORNER_RADIUS = 12
const MIN_CORNER_RADIUS = 0
const MAX_CORNER_RADIUS = 48
const DEFAULT_BORDER_WIDTH = 0
const DEFAULT_BORDER_COLOR = '#00000000'
const MIN_BORDER_WIDTH = 0
const MAX_BORDER_WIDTH = 24

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

interface CompetitorDraft {
  competitorId: string | null
  name: string
  score: number
  nameTextColor: string
  scoreTextColor: string
  imageUrl: string
  scoreImageUrl: string
  originalName?: string
  originalNameTextColor?: string
  originalScoreTextColor?: string
  originalImageUrl?: string
  originalScoreImageUrl?: string
}

export interface ScoreboardDraft {
  name: string
  title: string
  titleTextColor: string
  dividerColor: string
  backgroundColor: string
  competitors: CompetitorDraft[]
  backgroundImage: string
  backgroundImageFit: ImageFit
  cornerRadius: number
  borderWidth: number
  borderColor: string
  fontKey: ChatFontKey
}

const AVATAR_SIZE = SCOREBOARD.divider.height
const AVATAR_RING_WIDTH = 2
const AVATAR_RETRY_MS = 60_000

const CompetitorAvatar = ({
  src,
  ringColor,
  visible,
  onLoaded,
  onFail
}: {
  src: string
  ringColor: string
  visible: boolean
  onLoaded: () => void
  onFail: () => void
}): JSX.Element => (
  <img
    src={src}
    alt=''
    aria-hidden='true'
    onLoad={onLoaded}
    onError={onFail}
    style={{
      width: `${AVATAR_SIZE}px`,
      height: `${AVATAR_SIZE}px`,
      boxSizing: 'border-box',
      borderRadius: '50%',
      objectFit: 'cover',
      border: `${AVATAR_RING_WIDTH}px solid ${ringColor}`,
      alignSelf: 'center',
      flexShrink: 0,
      visibility: visible ? 'visible' : 'hidden'
    }}
  />
)

export interface ScoreboardLiveData {
  competitorScores: number[]
}

export const scoreboardLiveFromSummary = (s: ScoreboardSummary): ScoreboardLiveData => ({
  competitorScores: s.competitors.map((c) => c.score)
})

const TwoCompetitorBody = ({
  draft,
  live,
  rollDirection = 'auto'
}: {
  draft: ScoreboardDraft
  live?: ScoreboardLiveData
  rollDirection?: 'auto' | 'up'
}): JSX.Element => {
  const [a, b] = draft.competitors
  const scoreA = live?.competitorScores[0] ?? 0
  const scoreB = live?.competitorScores[1] ?? 0
  const [failedAvatarUrls, setFailedAvatarUrls] = useState<string[]>([])
  const [loadedAvatarUrls, setLoadedAvatarUrls] = useState<string[]>([])
  const markAvatarFailed = (url: string): void => {
    setFailedAvatarUrls((prev) => prev.includes(url) ? prev : [...prev, url])
  }
  const markAvatarLoaded = (url: string): void => {
    setLoadedAvatarUrls((prev) => prev.includes(url) ? prev : [...prev, url])
  }
  const avatarUrlA = a.imageUrl.trim()
  const avatarUrlB = b.imageUrl.trim()
  useEffect(() => {
    const keepCurrent = (prev: string[]): string[] => {
      const next = prev.filter((u) => u === avatarUrlA || u === avatarUrlB)
      return next.length === prev.length ? prev : next
    }
    setFailedAvatarUrls(keepCurrent)
    setLoadedAvatarUrls(keepCurrent)
  }, [avatarUrlA, avatarUrlB])
  useEffect(() => {
    if (failedAvatarUrls.length === 0) return undefined
    const timer = globalThis.setTimeout(() => { setFailedAvatarUrls([]) }, AVATAR_RETRY_MS)
    return () => { globalThis.clearTimeout(timer) }
  }, [failedAvatarUrls])
  const bothHaveAvatars = avatarUrlA !== '' && avatarUrlB !== ''
  const neitherAvatarFailed =
    !failedAvatarUrls.includes(avatarUrlA) && !failedAvatarUrls.includes(avatarUrlB)
  const showAvatars = bothHaveAvatars && neitherAvatarFailed
  useEffect(() => {
    if (!showAvatars) {
      setLoadedAvatarUrls((prev) => prev.length === 0 ? prev : [])
    }
  }, [showAvatars])
  const bothAvatarsLoaded =
    loadedAvatarUrls.includes(avatarUrlA) && loadedAvatarUrls.includes(avatarUrlB)
  const rowTop = SCOREBOARD.divider.top + (SCOREBOARD.divider.height - SCOREBOARD.score.lineHeight) / 2
  return (
    <>
      <div
        aria-hidden='true'
        style={{
          position: 'absolute',
          left: '50%',
          top: `${SCOREBOARD.divider.top}px`,
          width: '2px',
          height: `${SCOREBOARD.divider.height}px`,
          transform: 'translateX(-50%)',
          background: draft.dividerColor
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: 0,
          right: '50%',
          top: `${rowTop}px`,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'flex-end',
          gap: `${SCOREBOARD.name.scoreGap}px`,
          paddingRight: `${SCOREBOARD.divider.horizontalGap}px`,
          paddingLeft: '8px',
          whiteSpace: 'nowrap'
        }}
      >
        {showAvatars && (
          <CompetitorAvatar
            src={avatarUrlA}
            ringColor={a.nameTextColor}
            visible={bothAvatarsLoaded}
            onLoaded={() => markAvatarLoaded(avatarUrlA)}
            onFail={() => markAvatarFailed(avatarUrlA)}
          />
        )}
        <span
          style={{
            color: a.nameTextColor,
            fontWeight: SCOREBOARD.name.fontWeight,
            fontSize: `${SCOREBOARD.name.fontSize}px`,
            lineHeight: `${SCOREBOARD.name.lineHeight}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {a.name.length > 0 ? a.name : 'Team A'}
        </span>
        <span
          style={{
            color: a.scoreTextColor,
            fontWeight: SCOREBOARD.score.fontWeight,
            fontSize: `${SCOREBOARD.score.fontSize}px`,
            lineHeight: `${SCOREBOARD.score.lineHeight}px`,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          <RollingNumber value={scoreA} lineHeight={SCOREBOARD.score.lineHeight} direction={rollDirection} />
        </span>
      </div>

      <div
        style={{
          position: 'absolute',
          left: '50%',
          right: 0,
          top: `${rowTop}px`,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'flex-start',
          gap: `${SCOREBOARD.name.scoreGap}px`,
          paddingLeft: `${SCOREBOARD.divider.horizontalGap}px`,
          paddingRight: '8px',
          whiteSpace: 'nowrap'
        }}
      >
        <span
          style={{
            color: b.scoreTextColor,
            fontWeight: SCOREBOARD.score.fontWeight,
            fontSize: `${SCOREBOARD.score.fontSize}px`,
            lineHeight: `${SCOREBOARD.score.lineHeight}px`,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          <RollingNumber value={scoreB} lineHeight={SCOREBOARD.score.lineHeight} direction={rollDirection} />
        </span>
        <span
          style={{
            color: b.nameTextColor,
            fontWeight: SCOREBOARD.name.fontWeight,
            fontSize: `${SCOREBOARD.name.fontSize}px`,
            lineHeight: `${SCOREBOARD.name.lineHeight}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {b.name.length > 0 ? b.name : 'Team B'}
        </span>
        {showAvatars && (
          <CompetitorAvatar
            src={avatarUrlB}
            ringColor={b.nameTextColor}
            visible={bothAvatarsLoaded}
            onLoaded={() => markAvatarLoaded(avatarUrlB)}
            onFail={() => markAvatarFailed(avatarUrlB)}
          />
        )}
      </div>
    </>
  )
}

const ManyCompetitorBody = ({
  draft,
  live,
  rollDirection = 'auto'
}: {
  draft: ScoreboardDraft
  live?: ScoreboardLiveData
  rollDirection?: 'auto' | 'up'
}): JSX.Element => (
  <div
    style={{
      position: 'absolute',
      top: `${SCOREBOARD.divider.top}px`,
      left: 0,
      right: 0,
      height: `${SCOREBOARD.divider.height}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      padding: '0 10px',
      gap: '8px'
    }}
  >
    {draft.competitors.map((c, i) => {
      const score = live?.competitorScores[i] ?? 0
      return (
        <React.Fragment key={c.competitorId ?? `new-${i}`}>
          {i > 0 && (
            <div
              aria-hidden='true'
              style={{
                width: '2px',
                height: `${SCOREBOARD.divider.height}px`,
                background: draft.dividerColor
              }}
            />
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: `${SCOREBOARD.name.scoreGap}px`,
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
          >
            <span
              style={{
                color: c.nameTextColor,
                fontWeight: SCOREBOARD.name.fontWeight,
                fontSize: `${SCOREBOARD.name.fontSize}px`,
                lineHeight: `${SCOREBOARD.name.lineHeight}px`,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {c.name.length > 0 ? c.name : `Competitor ${i + 1}`}
            </span>
            <span
              style={{
                color: c.scoreTextColor,
                fontWeight: SCOREBOARD.score.fontWeight,
                fontSize: '28px',
                lineHeight: `${SCOREBOARD.divider.height}px`,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              <RollingNumber value={score} lineHeight={SCOREBOARD.divider.height} direction={rollDirection} />
            </span>
          </div>
        </React.Fragment>
      )
    })}
  </div>
)

export const ScoreboardPreview = ({
  draft,
  live,
  layoutWidth = SCOREBOARD.width
}: {
  draft: ScoreboardDraft
  live?: ScoreboardLiveData
  layoutWidth?: number
}): JSX.Element => {
  const fontFamily = elementFontStack(draft.fontKey)
  const fontReady = useFontGate(draft.fontKey)
  const hasBgImage = draft.backgroundImage.trim().length > 0
  const isTwoCompetitor = draft.competitors.length === 2
  const hasTitle = draft.title.trim().length > 0

  const [celebration, setCelebration] = useState<{ id: number, images: string[] } | null>(null)
  const prevScoresRef = useRef<number[] | null>(null)
  const celebIdRef = useRef(0)
  const [displayScores, setDisplayScores] = useState<number[] | null>(() => live?.competitorScores ?? null)
  const pendingScoresRef = useRef<number[] | null>(null)
  const revealSafetyRef = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined)
  const [rollDirection, setRollDirection] = useState<'auto' | 'up'>('auto')
  const scoresKey = (live?.competitorScores ?? []).join(',')
  useEffect(() => {
    const next = live?.competitorScores ?? null
    if (next === null) return
    pendingScoresRef.current = next
    const prev = prevScoresRef.current
    prevScoresRef.current = next
    if (prev === null) {
      setRollDirection('auto')
      setDisplayScores(next)
      return
    }
    const upIndices: number[] = []
    const len = Math.max(prev.length, next.length)
    for (let i = 0; i < len; i++) {
      if ((next[i] ?? 0) > (prev[i] ?? 0)) upIndices.push(i)
    }
    let images: string[] | null = null
    if (upIndices.length > 0) {
      const imgs = upIndices.map((i) => draft.competitors[i]?.scoreImageUrl?.trim() ?? '')
      if (upIndices.length === 1) {
        if (imgs[0].length > 0) images = imgs
      } else if (!imgs.some((s) => s.length === 0)) {
        images = imgs
      }
    }
    if (images !== null) {
      celebIdRef.current += 1
      setCelebration({ id: celebIdRef.current, images })
      if (revealSafetyRef.current !== undefined) globalThis.clearTimeout(revealSafetyRef.current)
      revealSafetyRef.current = globalThis.setTimeout(() => {
        revealSafetyRef.current = undefined
        if (pendingScoresRef.current !== null) {
          setRollDirection('up')
          setDisplayScores(pendingScoresRef.current)
        }
      }, SCOREBUG_COVER_MS + SCOREBUG_HOLD_MS + 800)
    } else {
      setRollDirection('auto')
      setDisplayScores(next)
    }
  }, [scoresKey])

  const handleReveal = useCallback(() => {
    if (revealSafetyRef.current !== undefined) {
      globalThis.clearTimeout(revealSafetyRef.current)
      revealSafetyRef.current = undefined
    }
    if (pendingScoresRef.current !== null) {
      setRollDirection('up')
      setDisplayScores(pendingScoresRef.current)
    }
  }, [])
  const handleDone = useCallback(() => setCelebration(null), [])

  useEffect(() => () => {
    if (revealSafetyRef.current !== undefined) globalThis.clearTimeout(revealSafetyRef.current)
  }, [])

  const bodyLive: ScoreboardLiveData | undefined =
    displayScores === null ? live : { ...live, competitorScores: displayScores }

  if (!fontReady) {
    return <CenteredOnCanvas width={layoutWidth} height={SCOREBOARD.height}>{null}</CenteredOnCanvas>
  }

  return (
    <CenteredOnCanvas width={layoutWidth} height={SCOREBOARD.height}>
      <div
        data-testid='scoreboard-preview'
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          fontFamily,
          overflow: 'hidden',
          borderRadius: `${draft.cornerRadius}px`,
          border: `${draft.borderWidth ?? DEFAULT_BORDER_WIDTH}px solid ${draft.borderColor ?? DEFAULT_BORDER_COLOR}`,
          background: draft.backgroundColor
        }}
      >
        {hasBgImage && (
          <img
            src={draft.backgroundImage}
            alt=''
            aria-hidden='true'
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: fitToObjectFit(draft.backgroundImageFit),
              zIndex: 0
            }}
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
          />
        )}

        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          {hasTitle && (
            <div
              style={{
                position: 'absolute',
                top: `${SCOREBOARD.title.paddingTop}px`,
                left: 0,
                right: 0,
                textAlign: 'center',
                color: draft.titleTextColor,
                fontWeight: SCOREBOARD.title.fontWeight,
                fontSize: `${SCOREBOARD.title.fontSize}px`,
                lineHeight: `${SCOREBOARD.title.fontSize}px`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 8px'
              }}
            >
              {draft.title}
            </div>
          )}

          {draft.competitors.length === 0
            ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontStyle: 'italic',
                  color: 'rgba(255,255,255,0.5)',
                  textAlign: 'center',
                  padding: '0 8px'
                }}
              >
                Add a competitor to see them here.
              </div>
              )
            : isTwoCompetitor
              ? <TwoCompetitorBody draft={draft} live={bodyLive} rollDirection={rollDirection} />
              : <ManyCompetitorBody draft={draft} live={bodyLive} rollDirection={rollDirection} />}
        </div>

        {celebration !== null && (
          <ScoreCelebration
            key={celebration.id}
            images={celebration.images}
            cornerRadius={draft.cornerRadius}
            onReveal={handleReveal}
            onDone={handleDone}
          />
        )}
      </div>
    </CenteredOnCanvas>
  )
}

const draftCompetitorFromServer = (c: Competitor): CompetitorDraft => ({
  competitorId: c.competitorId,
  name: c.name,
  score: c.score,
  nameTextColor: c.nameTextColor,
  scoreTextColor: c.scoreTextColor,
  imageUrl: c.imageUrl ?? '',
  scoreImageUrl: c.scoreImageUrl ?? '',
  originalName: c.name,
  originalNameTextColor: c.nameTextColor,
  originalScoreTextColor: c.scoreTextColor,
  originalImageUrl: c.imageUrl ?? '',
  originalScoreImageUrl: c.scoreImageUrl ?? ''
})

const emptyDraft = (): ScoreboardDraft => ({
  name: '',
  title: '',
  titleTextColor: DEFAULT_TITLE_TEXT_COLOR,
  dividerColor: DEFAULT_DIVIDER_COLOR,
  backgroundColor: DEFAULT_BG,
  competitors: DEFAULT_COMPETITORS.map((c) => ({
    competitorId: null,
    name: c.name,
    score: 0,
    nameTextColor: DEFAULT_NAME_TEXT_COLOR,
    scoreTextColor: DEFAULT_SCORE_TEXT_COLOR,
    imageUrl: '',
    scoreImageUrl: ''
  })),
  backgroundImage: '',
  backgroundImageFit: 'COVER',
  cornerRadius: DEFAULT_CORNER_RADIUS,
  borderWidth: DEFAULT_BORDER_WIDTH,
  borderColor: DEFAULT_BORDER_COLOR,
  fontKey: 'DEFAULT'
})

export const draftFromScoreboard = (s: ScoreboardSummary): ScoreboardDraft => ({
  name: s.name,
  title: s.title ?? '',
  titleTextColor: s.titleTextColor,
  dividerColor: s.dividerColor,
  backgroundColor: s.backgroundColor,
  competitors: s.competitors.map(draftCompetitorFromServer),
  backgroundImage: s.backgroundImage ?? '',
  backgroundImageFit: s.backgroundImageFit ?? 'COVER',
  cornerRadius: s.cornerRadius,
  borderWidth: s.borderWidth ?? DEFAULT_BORDER_WIDTH,
  borderColor: s.borderColor ?? DEFAULT_BORDER_COLOR,
  fontKey: normalizeChatFontKey(s.fontKey ?? 'DEFAULT')
})

const ScoreboardForm = ({
  existing,
  readOnly,
  refreshEvent,
  initialSavedNote = false,
  onCancel
}: {
  existing: ScoreboardSummary
  readOnly: boolean
  refreshEvent: () => Promise<void>
  initialSavedNote?: boolean
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { youtubeConnected } = useYouTubeConnection()
  const [draft, setDraft] = useState<ScoreboardDraft>(() => draftFromScoreboard(existing))
  const [removedIds, setRemovedIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(initialSavedNote)
  const [reseedPending, setReseedPending] = useState(false)
  const skipNoteClearRef = useRef(true)
  useEffect(() => {
    if (skipNoteClearRef.current) {
      skipNoteClearRef.current = false
      return
    }
    setSavedNote(false)
  }, [draft])
  useEffect(() => {
    if (!reseedPending) return
    skipNoteClearRef.current = true
    setDraft(draftFromScoreboard(existing))
    setReseedPending(false)
    setSavedNote(true)
  }, [reseedPending, existing])

  const updateCompetitor = (idx: number, patch: Partial<CompetitorDraft>): void => {
    setDraft((d) => ({
      ...d,
      competitors: d.competitors.map((c, i) => i === idx ? { ...c, ...patch } : c)
    }))
  }

  const addCompetitorRow = (): void => {
    setDraft((d) => ({
      ...d,
      competitors: [
        ...d.competitors,
        {
          competitorId: null,
          name: `Competitor ${d.competitors.length + 1}`,
          score: 0,
          nameTextColor: DEFAULT_NAME_TEXT_COLOR,
          scoreTextColor: DEFAULT_SCORE_TEXT_COLOR,
          imageUrl: '',
          scoreImageUrl: ''
        }
      ]
    }))
  }

  const removeCompetitorRow = (idx: number): void => {
    const target = draft.competitors[idx]
    if (target.competitorId !== null) {
      setRemovedIds((prev) => [...prev, target.competitorId as string])
    }
    setDraft((d) => ({ ...d, competitors: d.competitors.filter((_, i) => i !== idx) }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    if (draft.name.trim().length === 0) {
      setError('Scoreboard name is required.')
      return
    }
    if (draft.competitors.some((c) => c.name.trim().length === 0)) {
      setError('Every competitor needs a name.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const ops: Array<Promise<unknown>> = []

      const boardChanged =
        draft.name !== existing.name ||
        draft.title !== (existing.title ?? '') ||
        draft.titleTextColor !== existing.titleTextColor ||
        draft.dividerColor !== existing.dividerColor ||
        draft.backgroundColor !== existing.backgroundColor ||
        draft.backgroundImage !== (existing.backgroundImage ?? '') ||
        draft.backgroundImageFit !== (existing.backgroundImageFit ?? 'COVER') ||
        draft.cornerRadius !== existing.cornerRadius ||
        draft.borderWidth !== (existing.borderWidth ?? DEFAULT_BORDER_WIDTH) ||
        draft.borderColor !== (existing.borderColor ?? DEFAULT_BORDER_COLOR) ||
        draft.fontKey !== normalizeChatFontKey(existing.fontKey ?? 'DEFAULT')
      if (boardChanged) {
        ops.push(graphqlRequest(updateScoreboardMutation, {
          boardId: existing.boardId,
          name: draft.name,
          title: draft.title.trim().length > 0 ? draft.title : null,
          titleTextColor: draft.titleTextColor,
          dividerColor: draft.dividerColor,
          backgroundColor: draft.backgroundColor,
          backgroundImage: draft.backgroundImage.trim().length > 0
            ? draft.backgroundImage
            : null,
          backgroundImageFit: draft.backgroundImage.trim().length > 0
            ? draft.backgroundImageFit
            : null,
          cornerRadius: draft.cornerRadius,
          borderWidth: draft.borderWidth,
          borderColor: draft.borderColor,
          fontKey: draft.fontKey === 'DEFAULT' ? null : draft.fontKey
        }, auth0Token))
      }

      for (const c of draft.competitors) {
        if (c.competitorId === null) {
          ops.push(graphqlRequest(addCompetitorMutation, {
            boardId: existing.boardId,
            name: c.name,
            score: 0,
            nameTextColor: c.nameTextColor,
            scoreTextColor: c.scoreTextColor,
            imageUrl: c.imageUrl.trim().length > 0 ? c.imageUrl : null,
            scoreImageUrl: c.scoreImageUrl.trim().length > 0 ? c.scoreImageUrl : null
          }, auth0Token))
        } else if (
          c.name !== c.originalName ||
          c.nameTextColor !== c.originalNameTextColor ||
          c.scoreTextColor !== c.originalScoreTextColor ||
          c.imageUrl !== c.originalImageUrl ||
          c.scoreImageUrl !== c.originalScoreImageUrl
        ) {
          let scoreToSend: number
          if (typeof c.score === 'number') {
            scoreToSend = c.score
          } else {
            const existingMatch = existing.competitors.find(
              (ec) => ec.competitorId === c.competitorId
            )
            scoreToSend = existingMatch?.score ?? 0
            console.warn(
              'ScoreboardForm: CompetitorDraft.score was non-number on save; ' +
              'falling back to server score',
              { competitorId: c.competitorId, draftScore: c.score, fallback: scoreToSend }
            )
          }
          ops.push(graphqlRequest(updateCompetitorMutation, {
            boardId: existing.boardId,
            competitorId: c.competitorId,
            name: c.name,
            score: scoreToSend,
            nameTextColor: c.nameTextColor,
            scoreTextColor: c.scoreTextColor,
            imageUrl: c.imageUrl.trim().length > 0 ? c.imageUrl : null,
            scoreImageUrl: c.scoreImageUrl.trim().length > 0 ? c.scoreImageUrl : null
          }, auth0Token))
        }
      }
      for (const id of removedIds) {
        ops.push(graphqlRequest(removeCompetitorMutation, {
          boardId: existing.boardId,
          competitorId: id
        }, auth0Token))
      }

      await Promise.all(ops)

      try {
        await refreshEvent()
      } catch {
        onCancel()
        return
      }
      setRemovedIds([])
      setReseedPending(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scoreboard')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset disabled={readOnly} className='contents space-y-1.5'>
          <SectionCard
            title={readOnly ? 'View scoreboard' : 'Edit scoreboard'}
            action={
            readOnly
              ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                  <EyeIcon className='size-3' aria-hidden='true' />
                  Read-only
                </span>
                )
              : (
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to list
                </button>
                )
          }
          >
            <label className='mb-4 block'>
              <FieldLabel>Scoreboard name (admin label)</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Final round leaderboard'
                required
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            <label className='block'>
              <FieldLabel>Title (optional, shows at the top of the overlay)</FieldLabel>
              <input
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder='Leave empty to hide'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
          </SectionCard>

          <SectionCard
            title='Competitors'
            action={
              <button
                type='button'
                onClick={addCompetitorRow}
                disabled={draft.competitors.length >= MAX_COMPETITORS}
                title={draft.competitors.length >= MAX_COMPETITORS ? `Max ${MAX_COMPETITORS} competitors` : undefined}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add competitor
              </button>
          }
          >
            {draft.competitors.length === 0
              ? (
                <div className='rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary'>
                  No competitors. Add one above.
                </div>
                )
              : (
                <ul className='space-y-3'>
                  {draft.competitors.map((c, i) => (
                    <li
                      key={c.competitorId ?? `new-${i}`}
                      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'
                    >
                      <div className='mb-2 flex items-center justify-between'>
                        <span className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                          Competitor {i + 1}{c.competitorId === null && ' (new)'}
                        </span>
                        <button
                          type='button'
                          onClick={() => removeCompetitorRow(i)}
                          title='Remove'
                          className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-dm-text-secondary hover:border-red-500/40 hover:text-red-300'
                        >
                          <TrashIcon className='size-3' aria-hidden='true' />
                          Remove
                        </button>
                      </div>
                      <label className='mb-3 block'>
                        <FieldLabel>Name</FieldLabel>
                        <input
                          value={c.name}
                          onChange={(e) => updateCompetitor(i, { name: e.target.value })}
                          required
                          className='w-full rounded-md border border-white/10 bg-dm-bg-secondary px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                        />
                      </label>
                      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                        <ColorInput
                          label='Name text color'
                          value={c.nameTextColor}
                          onChange={(v) => updateCompetitor(i, { nameTextColor: v })}
                        />
                        <ColorInput
                          label='Score text color'
                          value={c.scoreTextColor}
                          onChange={(v) => updateCompetitor(i, { scoreTextColor: v })}
                        />
                      </div>
                      <div className='mt-3'>
                        <ImageUpload
                          value={c.imageUrl}
                          onChange={(v) => updateCompetitor(i, { imageUrl: v })}
                          kind='SCOREBOARD_AVATAR'
                          shape='circle'
                          label='Avatar (optional, outlined with the name color)'
                          helpText='Square images render best as a circle. ≤ 4 MB.'
                        />
                      </div>
                      <div className='mt-3'>
                        <ImageUpload
                          value={c.scoreImageUrl}
                          onChange={(v) => updateCompetitor(i, { scoreImageUrl: v })}
                          kind='SCOREBOARD_SCORE_IMAGE'
                          label='Score image (optional)'
                          helpText='When this competitor scores, this image briefly covers the scoreboard like a scorebug, then sweeps away to reveal the new score. ≤ 8 MB.'
                        />
                      </div>
                    </li>
                  ))}
                </ul>
                )}
          </SectionCard>

          <SectionCard title='Scoreboard styling'>
            <div className='space-y-4'>
              <label className='block'>
                <FieldLabel>Font</FieldLabel>

                <FontSelect
                  value={draft.fontKey}
                  onChange={(next) => setDraft((d) => ({ ...d, fontKey: next as ChatFontKey }))}
                  defaultLabel='Default (standard element font)'
                  className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </label>
              <ColorInput
                label='Title text color'
                value={draft.titleTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, titleTextColor: v }))}
              />
              <ColorInput
                label='Divider color (between scores / rows)'
                value={draft.dividerColor}
                onChange={(v) => setDraft((d) => ({ ...d, dividerColor: v }))}
              />
              <BackgroundInput
                label='Scoreboard background'
                value={draft.backgroundColor}
                onChange={(v) => setDraft((d) => ({ ...d, backgroundColor: v }))}
              />
              <label className='block'>
                <FieldLabel>Container corner radius (px)</FieldLabel>
                <div className='flex items-center gap-2'>
                  <input
                    type='range'
                    min={MIN_CORNER_RADIUS}
                    max={MAX_CORNER_RADIUS}
                    value={draft.cornerRadius}
                    onChange={(e) => setDraft((d) => ({ ...d, cornerRadius: Number.parseInt(e.target.value, 10) }))}
                    className='flex-1 accent-brand'
                  />
                  <NumberInput
                    value={draft.cornerRadius}
                    min={MIN_CORNER_RADIUS}
                    max={MAX_CORNER_RADIUS}
                    onCommit={(n) => setDraft((d) => ({ ...d, cornerRadius: n }))}
                    className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                </div>
              </label>
              <label className='block'>
                <FieldLabel>Border width (px)</FieldLabel>
                <NumberInput
                  value={draft.borderWidth}
                  min={MIN_BORDER_WIDTH}
                  max={MAX_BORDER_WIDTH}
                  onCommit={(n) => setDraft((d) => ({ ...d, borderWidth: n }))}
                  className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </label>
              <div>
                <ColorInput
                  label='Border color'
                  value={draft.borderColor}
                  onChange={(v) => setDraft((d) => ({ ...d, borderColor: v }))}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard title='Overlay background image'>
            <BackgroundImageInput
              url={draft.backgroundImage}
              fit={draft.backgroundImageFit}
              onChangeUrl={(v) => setDraft((d) => ({ ...d, backgroundImage: v }))}
              onChangeFit={(v) => setDraft((d) => ({ ...d, backgroundImageFit: v }))}
              kind='SCOREBOARD_BACKGROUND'
              label='Background image (optional)'
            />
          </SectionCard>

        </fieldset>
      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          {readOnly
            ? (
              <>
                <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  This stream has ended, so its scoreboards are read-only. The
                  preview below shows the scoreboard&apos;s last score.
                </div>
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to list
                </button>
              </>
              )
            : (
              <div className='flex items-center gap-2'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {submitting && <LoadingSpinner />}
                  Save changes
                </button>
                <button
                  type='button'
                  onClick={onCancel}
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
              )}
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='scoreboard-saved' />}
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>
          <ScaledStage mode='preview' showCheckerboard elementWidth={SCOREBOARD.width} elementHeight={SCOREBOARD.height + (youtubeConnected ? 26 : 0)}>
            <ScoreboardPreview draft={draft} live={readOnly ? scoreboardLiveFromSummary(existing) : undefined} />
          </ScaledStage>
          <p className='text-[11px] text-dm-text-accent'>
            {readOnly
              ? 'Shows the scoreboard’s last recorded score.'
              : 'Scores show 0 until a future update flow lets you adjust them manually or hook in chat-driven scoring.'}
          </p>
        </div>
      </div>
    </form>
  )
}

const CreateForm = ({
  eventId,
  onSaved,
  onCancel
}: {
  eventId: string
  onSaved: (sb: ScoreboardSummary) => void
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { youtubeConnected } = useYouTubeConnection()
  const [draft, setDraft] = useState<ScoreboardDraft>(emptyDraft())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateCompetitor = (idx: number, patch: Partial<CompetitorDraft>): void => {
    setDraft((d) => ({
      ...d,
      competitors: d.competitors.map((c, i) => i === idx ? { ...c, ...patch } : c)
    }))
  }

  const addCompetitorRow = (): void => {
    setDraft((d) => ({
      ...d,
      competitors: [
        ...d.competitors,
        {
          competitorId: null,
          name: `Competitor ${d.competitors.length + 1}`,
          score: 0,
          nameTextColor: DEFAULT_NAME_TEXT_COLOR,
          scoreTextColor: DEFAULT_SCORE_TEXT_COLOR,
          imageUrl: '',
          scoreImageUrl: ''
        }
      ]
    }))
  }

  const removeCompetitorRow = (idx: number): void => {
    setDraft((d) => ({ ...d, competitors: d.competitors.filter((_, i) => i !== idx) }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    if (draft.name.trim().length === 0) {
      setError('Scoreboard name is required.')
      return
    }
    if (draft.competitors.some((c) => c.name.trim().length === 0)) {
      setError('Every competitor needs a name.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const competitorsInput: CompetitorInput[] = draft.competitors.map((c) => ({
        name: c.name,
        nameTextColor: c.nameTextColor,
        scoreTextColor: c.scoreTextColor,
        imageUrl: c.imageUrl.trim().length > 0 ? c.imageUrl : null,
        scoreImageUrl: c.scoreImageUrl.trim().length > 0 ? c.scoreImageUrl : null
      }))
      const { scoreboard } = await graphqlRequest<{ scoreboard: ScoreboardSummary }>(
        createScoreboardMutation,
        {
          eventId,
          name: draft.name,
          title: draft.title.trim().length > 0 ? draft.title : null,
          titleTextColor: draft.titleTextColor,
          dividerColor: draft.dividerColor,
          backgroundColor: draft.backgroundColor,
          backgroundImage: draft.backgroundImage.trim().length > 0
            ? draft.backgroundImage
            : null,
          backgroundImageFit: draft.backgroundImage.trim().length > 0
            ? draft.backgroundImageFit
            : null,
          cornerRadius: draft.cornerRadius,
          borderWidth: draft.borderWidth,
          borderColor: draft.borderColor,
          fontKey: draft.fontKey === 'DEFAULT' ? null : draft.fontKey,
          competitors: competitorsInput
        },
        auth0Token
      )
      onSaved(scoreboard)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create scoreboard')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <SectionCard
          title='New scoreboard'
          action={
            <button
              type='button'
              onClick={onCancel}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
              Back to list
            </button>
          }
        >
          <label className='mb-4 block'>
            <FieldLabel>Scoreboard name (admin label)</FieldLabel>
            <input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder='Final round leaderboard'
              required
              className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
            />
          </label>
          <label className='block'>
            <FieldLabel>Title (optional)</FieldLabel>
            <input
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder='Leave empty to hide'
              className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
            />
          </label>
        </SectionCard>

        <SectionCard
          title='Competitors'
          action={
            <button
              type='button'
              onClick={addCompetitorRow}
              disabled={draft.competitors.length >= MAX_COMPETITORS}
              title={draft.competitors.length >= MAX_COMPETITORS ? `Max ${MAX_COMPETITORS} competitors` : undefined}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              Add competitor
            </button>
          }
        >
          {draft.competitors.length === 0
            ? (
              <div className='rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary'>
                No competitors. Add one above.
              </div>
              )
            : (
              <ul className='space-y-3'>
                {draft.competitors.map((c, i) => (
                  <li
                    key={`new-${i}`}
                    className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'
                  >
                    <div className='mb-2 flex items-center justify-between'>
                      <span className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                        Competitor {i + 1}
                      </span>
                      <button
                        type='button'
                        onClick={() => removeCompetitorRow(i)}
                        title='Remove'
                        className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-dm-text-secondary hover:border-red-500/40 hover:text-red-300'
                      >
                        <TrashIcon className='size-3' aria-hidden='true' />
                        Remove
                      </button>
                    </div>
                    <label className='mb-3 block'>
                      <FieldLabel>Name</FieldLabel>
                      <input
                        value={c.name}
                        onChange={(e) => updateCompetitor(i, { name: e.target.value })}
                        required
                        className='w-full rounded-md border border-white/10 bg-dm-bg-secondary px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                      />
                    </label>
                    <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                      <ColorInput
                        label='Name text color'
                        value={c.nameTextColor}
                        onChange={(v) => updateCompetitor(i, { nameTextColor: v })}
                      />
                      <ColorInput
                        label='Score text color'
                        value={c.scoreTextColor}
                        onChange={(v) => updateCompetitor(i, { scoreTextColor: v })}
                      />
                    </div>
                    <div className='mt-3'>
                      <ImageUpload
                        value={c.imageUrl}
                        onChange={(v) => updateCompetitor(i, { imageUrl: v })}
                        kind='SCOREBOARD_AVATAR'
                        shape='circle'
                        label='Avatar (optional, renders outlined with the name color)'
                        helpText='Square images render best as a circle. ≤ 4 MB.'
                      />
                    </div>
                    <div className='mt-3'>
                      <ImageUpload
                        value={c.scoreImageUrl}
                        onChange={(v) => updateCompetitor(i, { scoreImageUrl: v })}
                        kind='SCOREBOARD_SCORE_IMAGE'
                        label='Score image (optional)'
                        helpText='When this competitor scores, this image briefly covers the scoreboard like a scorebug, then sweeps away to reveal the new score. ≤ 8 MB.'
                      />
                    </div>
                  </li>
                ))}
              </ul>
              )}
        </SectionCard>

        <SectionCard title='Scoreboard styling'>
          <div className='space-y-4'>
            <label className='block'>
              <FieldLabel>Font</FieldLabel>

              <FontSelect
                value={draft.fontKey}
                onChange={(next) => setDraft((d) => ({ ...d, fontKey: next as ChatFontKey }))}
                defaultLabel='Default (standard element font)'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            <ColorInput
              label='Title text color'
              value={draft.titleTextColor}
              onChange={(v) => setDraft((d) => ({ ...d, titleTextColor: v }))}
            />
            <ColorInput
              label='Divider color (between scores / rows)'
              value={draft.dividerColor}
              onChange={(v) => setDraft((d) => ({ ...d, dividerColor: v }))}
            />
            <BackgroundInput
              label='Scoreboard background'
              value={draft.backgroundColor}
              onChange={(v) => setDraft((d) => ({ ...d, backgroundColor: v }))}
            />
            <label className='block'>
              <FieldLabel>Container corner radius (px)</FieldLabel>
              <div className='flex items-center gap-2'>
                <input
                  type='range'
                  min={MIN_CORNER_RADIUS}
                  max={MAX_CORNER_RADIUS}
                  value={draft.cornerRadius}
                  onChange={(e) => setDraft((d) => ({ ...d, cornerRadius: Number.parseInt(e.target.value, 10) }))}
                  className='flex-1 accent-brand'
                />
                <NumberInput
                  value={draft.cornerRadius}
                  min={MIN_CORNER_RADIUS}
                  max={MAX_CORNER_RADIUS}
                  onCommit={(n) => setDraft((d) => ({ ...d, cornerRadius: n }))}
                  className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </div>
            </label>
            <label className='block'>
              <FieldLabel>Border width (px)</FieldLabel>
              <NumberInput
                value={draft.borderWidth}
                min={MIN_BORDER_WIDTH}
                max={MAX_BORDER_WIDTH}
                onCommit={(n) => setDraft((d) => ({ ...d, borderWidth: n }))}
                className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            <div>
              <ColorInput
                label='Border color'
                value={draft.borderColor}
                onChange={(v) => setDraft((d) => ({ ...d, borderColor: v }))}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard title='Overlay background image'>
          <BackgroundImageInput
            url={draft.backgroundImage}
            fit={draft.backgroundImageFit}
            onChangeUrl={(v) => setDraft((d) => ({ ...d, backgroundImage: v }))}
            onChangeFit={(v) => setDraft((d) => ({ ...d, backgroundImageFit: v }))}
            kind='SCOREBOARD_BACKGROUND'
            label='Background image (optional)'
          />
        </SectionCard>

      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          <div className='flex items-center gap-2'>
            <button
              type='submit'
              disabled={submitting}
              className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {submitting && <LoadingSpinner />}
              Create scoreboard
            </button>
            <button
              type='button'
              onClick={onCancel}
              disabled={submitting}
              className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
            >
              Cancel
            </button>
          </div>
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>
          <ScaledStage mode='preview' showCheckerboard elementWidth={SCOREBOARD.width} elementHeight={SCOREBOARD.height + (youtubeConnected ? 26 : 0)}>
            <ScoreboardPreview draft={draft} />
          </ScaledStage>
        </div>
      </div>
    </form>
  )
}

const ScoreboardListRow = ({
  scoreboard,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  readOnly
}: {
  scoreboard: ScoreboardSummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  readOnly: boolean
}): JSX.Element => (
  <li
    data-testid={`scoreboard-row-${scoreboard.boardId}`}
    className='flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'
  >
    <div className='flex min-w-0 items-center gap-3'>
      <div
        className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md ring-1 ring-white/10'
        style={{ background: scoreboard.backgroundColor }}
      >
        <TrophyIcon className='size-5 text-brand' aria-hidden='true' />
      </div>
      <div className='flex min-w-0 flex-col leading-tight'>
        <span className='truncate text-sm font-medium text-dm-text-primary'>
          {scoreboard.name}
        </span>
        <span className='truncate text-[11px] text-dm-text-accent'>
          {scoreboard.competitors.length} competitor{scoreboard.competitors.length === 1 ? '' : 's'}
          {scoreboard.title !== null && scoreboard.title.length > 0
            ? <> · titled <span className='text-dm-text-secondary'>{scoreboard.title}</span></>
            : null}
        </span>
      </div>
    </div>
    <div className='flex shrink-0 items-center gap-1.5'>
      <button
        type='button'
        onClick={onEdit}
        disabled={busyAction !== null}
        title={readOnly ? 'View' : 'Edit'}
        className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
      >
        {readOnly
          ? <EyeIcon className='size-3.5' aria-hidden='true' />
          : <PencilSquareIcon className='size-3.5' aria-hidden='true' />}
        {readOnly ? 'View' : 'Edit'}
      </button>
      {!readOnly && (
        <button
          type='button'
          onClick={onDuplicate}
          title='Duplicate'
          disabled={busyAction !== null}
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
          Duplicate
        </button>
      )}
      <button
        type='button'
        onClick={onDelete}
        disabled={busyAction !== null}
        title='Delete'
        className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
      >
        {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
        Delete
      </button>
    </div>
  </li>
)

type Mode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit', boardId: string, justCreated?: boolean }

export const ScoreboardPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const isEnded = event.status === 'ENDED'
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [rowBusy, setRowBusy] = useState<{ boardId: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const editing = useMemo<ScoreboardSummary | null>(() => {
    if (mode.kind !== 'edit') return null
    return event.scoreboards.find((s) => s.boardId === mode.boardId) ?? null
  }, [mode, event.scoreboards])

  const upsertScoreboard = (sb: ScoreboardSummary): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          scoreboards: prev.scoreboards.some((s) => s.boardId === sb.boardId)
            ? prev.scoreboards.map((s) => s.boardId === sb.boardId ? sb : s)
            : [...prev.scoreboards, sb]
        }))
  }

  const removeScoreboard = (boardId: string): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          scoreboards: prev.scoreboards.filter((s) => s.boardId !== boardId)
        }))
  }

  const handleDelete = async (sb: ScoreboardSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    const ok = globalThis.confirm(
      sb.competitors.some((c) => c.score !== 0)
        ? `Delete scoreboard "${sb.name}"?\n\nWARNING: this scoreboard has scores that aren't 0/0. Deleting it permanently destroys the scoreboard and its competitors. (Use Reset scores to set everything back to 0 first if you want to keep it.)\n\nDelete anyway?`
        : `Delete scoreboard "${sb.name}"? Competitors will be deleted too.`
    )
    if (!ok) return
    setRowBusy({ boardId: sb.boardId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteScoreboardMutation, { boardId: sb.boardId }, auth0Token
      )
      if (result.deleted) removeScoreboard(sb.boardId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete scoreboard')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (sb: ScoreboardSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    setRowBusy({ boardId: sb.boardId, action: 'duplicate' })
    setRowError(null)
    try {
      const { scoreboard: created } = await graphqlRequest<{ scoreboard: ScoreboardSummary }>(
        duplicateScoreboardMutation, { boardId: sb.boardId }, auth0Token
      )
      upsertScoreboard(created)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate scoreboard')
    } finally {
      setRowBusy(null)
    }
  }

  if (mode.kind === 'create') {
    return (
      <CreateForm
        eventId={event.eventId}
        onSaved={(sb) => { upsertScoreboard(sb); setMode({ kind: 'edit', boardId: sb.boardId, justCreated: true }) }}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  if (mode.kind === 'edit') {
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That scoreboard no longer exists. Returning to the list…</ErrorInline>
          <button onClick={() => setMode({ kind: 'list' })} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Back to list
          </button>
        </div>
      )
    }
    return (
      <ScoreboardForm
        existing={editing}
        readOnly={isEnded}
        refreshEvent={refetchEvent}
        initialSavedNote={mode.justCreated === true}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Scoreboards'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void refetchEvent() }}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowPathIcon className='size-3.5' aria-hidden='true' />
              Refresh
            </button>
            {!isEnded && (
              <button
                type='button'
                data-testid='add-scoreboard-button'
                onClick={() => setMode({ kind: 'create' })}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add new scoreboard
              </button>
            )}
          </div>
        }
      >
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {event.scoreboards.length === 0
          ? (
            <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
              <TrophyIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
              <p>{isEnded ? 'This stream ended with no scoreboards.' : 'No scoreboards yet for this stream.'}</p>
              {!isEnded && (
                <button
                  type='button'
                  onClick={() => setMode({ kind: 'create' })}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
                >
                  <PlusIcon className='size-3.5' aria-hidden='true' />
                  Create the first one
                </button>
              )}
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='scoreboards-list'>
              {sortByName(event.scoreboards, (s) => s.name, (s) => s.boardId).map((sb) => (
                <ScoreboardListRow
                  key={sb.boardId}
                  scoreboard={sb}
                  onEdit={() => setMode({ kind: 'edit', boardId: sb.boardId })}
                  onDuplicate={() => { void handleDuplicate(sb) }}
                  onDelete={() => { void handleDelete(sb) }}
                  busyAction={rowBusy?.boardId === sb.boardId ? rowBusy.action : null}
                  readOnly={isEnded}
                />
              ))}
            </ul>
            )}
      </SectionCard>
    </div>
  )
}
